/**
 * BPF Memorial Canvas — Phase 3.0 (breed-specific layered renderer proof
 * path: Dog -> Pitbull -> Vault only) + Phase 3.0.1 + Phase 3.2.1.
 *
 * Phase 3.0.1: _positionLayer() and the per-entry x/y/width/height it
 * applied (including Phase 3.0's measured y:-4 compensation for the
 * original Pitbull exports) are removed entirely, not replaced with new
 * values. The merchant's asset workflow now aligns every layer to the
 * same shared canvas before export, so _applyLayer() below only ever
 * swaps `src` — every layer's box (position/size) is set once, uniformly,
 * in CSS (.bpf-memorial-canvas__layer in assets/bpf-memorial-designer.css)
 * and never touched per-asset. Nothing else in this file changed: same
 * public interface, same stale-load protection, same preload-before-swap
 * pattern, same z-index/status-overlay behavior.
 *
 * Phase 3.2.1: adds a second, mutually-exclusive composition path for
 * Capsule (Dog -> Pitbull -> Capsule) — one already-finished flattened
 * product image instead of Vault's base+figure pair. render() now
 * branches on the resolved visual record's own compositionMode
 * ("flattened" vs. the implicit "layered" default), never on
 * companion/breed, and hides whichever pair of layers doesn't apply.
 * _applyLayer()/_hideLayer()/_showStatus()/_hideStatus() are reused
 * as-is, unchanged; the Vault ("layered") branch's logic is identical to
 * before, just relocated into the else half of the new if/else. A third
 * layer element (capsuleEl) and its own preload-cache slot
 * (_lastCapsuleSrc) were added alongside the existing base/figure ones.
 *
 * This file is deliberately separate from assets/bpf-memorial-designer.js
 * and owns exactly one responsibility: turning a normalized canvas state
 * object into visible layers. It never queries a builder form field,
 * never reads that other file's `this.state`, never resolves a Shopify
 * product, and never touches validation, Add to Cart, or line-item
 * properties — assets/bpf-memorial-designer.js remains the only place
 * any of that happens. This class only ever receives whatever
 * getCanvasState() there builds and hands to render().
 *
 * Classic script, not an ES module (this theme doesn't use them) — the
 * class below is attached to `window` explicitly so it's referenceable
 * by name from bpf-memorial-designer.js regardless of exactly how each
 * script tag was loaded, as long as this file's <script> tag is ordered
 * first (see the comment beside both tags in sections/bpf-memorial-
 * designer.liquid).
 *
 * Layer stack, z-index driven by CSS (assets/bpf-memorial-designer.css),
 * never relied on via DOM order alone: figure (highest) -> Vault base ->
 * stage -> status overlay (unavailable/error), which sits BELOW the
 * stage by default and is only promoted above it, via a modifier class,
 * for as long as it's actually showing a message meant to replace the
 * normal composition. The stage image itself is static (set directly in
 * Liquid, never touched by this file) and is not part of canvas state.
 *
 * Stale-load protection: every render() call increments a generation
 * counter; each layer's own preload (an offscreen Image(), never the
 * visible <img> itself, so a failed load can never surface a broken-
 * image icon) captures that generation and discards its result if a
 * newer render() has since started — this is what "no stale previous
 * image after selections change" and "no broken-image icon" both come
 * from, not two separate mechanisms.
 *
 * Mobile hidden-mount lifecycle: render() never checks whether this
 * element is currently connected/visible, and never calls a layout-
 * dependent API (getBoundingClientRect, offsetWidth, etc.) — being
 * parked inside the permanently-hidden mobile holding mount
 * (data-canvas-holding-mount, display:none) does not stop `src`/`hidden`
 * updates from applying correctly. Renderer state (which layer elements
 * exist, what they're currently showing) lives on this instance and
 * the DOM nodes it was constructed with — moving those same nodes
 * between mounts (updateCanvasPosition() in bpf-memorial-designer.js, a
 * real DOM move, unchanged by this phase) never recreates or resets it.
 */

if (typeof window.BpfMemorialCanvasRenderer === 'undefined') {
  window.BpfMemorialCanvasRenderer = class BpfMemorialCanvasRenderer {
    constructor(rootElement) {
      this.root = rootElement || null;
      this.baseEl = this.root ? this.root.querySelector('[data-canvas-base]') : null;
      this.figureEl = this.root ? this.root.querySelector('[data-canvas-figure]') : null;
      this.capsuleEl = this.root ? this.root.querySelector('[data-canvas-capsule]') : null;
      this.statusEl = this.root ? this.root.querySelector('[data-canvas-status]') : null;
      // Phase 4D.10C — Picture Frame live layers. Both null when the
      // feature flag is off (Liquid never renders them), matching the
      // null-when-absent convention every other layer element above
      // already uses.
      this.frameEl = this.root ? this.root.querySelector('[data-canvas-frame]') : null;
      this.framePhotoCanvas = this.root ? this.root.querySelector('[data-canvas-frame-photo]') : null;

      this._generation = 0;
      this._lastBaseSrc = null;
      this._lastFigureSrc = null;
      this._lastCapsuleSrc = null;
      this._lastFrameSrc = null;
      // Object URL lifecycle for the customer's own uploaded photo (Part
      // 25 — never re-uploads for preview, revokes safely). Keyed off
      // the exact File reference so an unchanged File across renders
      // never creates a redundant object URL/reload.
      this._framePhotoFile = null;
      this._framePhotoObjectUrl = null;
    }

    // Single public entry point. Always safe to call regardless of
    // whether this.root is currently connected/visible in the document.
    render(canvasState) {
      if (!this.root) return;
      this._generation += 1;
      const generation = this._generation;

      const state = canvasState || {};

      // Resolved-but-unavailable product: replace the normal composition
      // entirely (every product layer hidden, status promoted above the
      // stage) — never keep showing a previously valid memorial as
      // though it still applies.
      if (state.isResolved && state.isAvailable === false) {
        this._hideLayer(this.baseEl);
        this._hideLayer(this.figureEl);
        this._hideLayer(this.capsuleEl);
        this._showStatus('unavailable');
        return;
      }

      // Not yet resolved, or resolved but no matching visual record —
      // both render identically: the static stage alone, nothing
      // overlaid. This is also the correct initial/neutral state.
      if (!state.isResolved || !state.visualAssets) {
        this._hideLayer(this.baseEl);
        this._hideLayer(this.figureEl);
        this._hideLayer(this.capsuleEl);
        this._hideStatus();
        return;
      }

      this._hideStatus();

      // Milestone 1 (Memorial Renderer): entry RESOLUTION (which
      // layer(s), which src, given visualAssets + the chosen colors) is
      // now a static, pure method — see its own comment below — so
      // assets/bpf-memorial-renderer.js can ask the identical question
      // without duplicating this branch. This method still owns 100% of
      // the DOM APPLICATION (hide/show/src-swap on real <img> elements)
      // exactly as before; only the lookup moved, not the control flow.
      const plan = BpfMemorialCanvasRenderer.resolveLayerPlan(state.visualAssets, state);

      // Phase 3.2.1: Vault (base+figure, two layers) and Capsule (one
      // already-finished flattened product image) are mutually
      // exclusive presentations, selected by the resolved visual
      // record's own compositionMode — never by companion/breed, since
      // a future breed could theoretically support either. Whichever
      // mode doesn't apply gets its layer(s) explicitly hidden here
      // (not just left unset) so switching Memorial Style back and forth
      // can never leave a stale layer from the other mode visible. Each
      // layer's own src/hidden state and preload cache (_lastBaseSrc/
      // _lastFigureSrc/_lastCapsuleSrc) are untouched when hidden this
      // way, so switching back to a previously-shown color redisplays it
      // instantly without a redundant reload.
      if (plan.compositionMode === 'flattened') {
        this._hideLayer(this.baseEl);
        this._hideLayer(this.figureEl);
        this._applyLayer(this.capsuleEl, plan.capsule, generation, '_lastCapsuleSrc');
      } else {
        this._hideLayer(this.capsuleEl);
        this._applyLayer(this.baseEl, plan.base, generation, '_lastBaseSrc');
        this._applyLayer(this.figureEl, plan.figure, generation, '_lastFigureSrc');
      }

      // Phase 4D.10C, Part 29 — independent of the Memorial resolution
      // branch above (matches renderMemorialPreviewCanvas()'s own "frame
      // is independent of Memorial product resolution" rule in assets/
      // bpf-memorial-renderer.js): a selected Picture Frame renders
      // regardless of whether a Vault/Capsule composition also applies.
      this._renderFrameLayers(state, generation);
    }

    destroy() {
      // No timers/observers currently held beyond the generation counter
      // (which simply stops mattering once this instance is discarded) —
      // this exists as the documented, always-safe cleanup hook for
      // whatever Phase 3.1+ adds, so bpf-memorial-designer.js never needs
      // to change its disconnectedCallback() contract again.
      this._generation += 1;
      this._releaseFramePhotoObjectUrl();
    }

    _releaseFramePhotoObjectUrl() {
      if (!this._framePhotoObjectUrl) return;
      try {
        URL.revokeObjectURL(this._framePhotoObjectUrl);
      } catch (error) {
        // best-effort cleanup only
      }
      this._framePhotoObjectUrl = null;
      this._framePhotoFile = null;
    }

    // Phase 4D.10C, Parts 22/25/26/29 — live Picture Frame rendering.
    // Async (real image loads), but render() above never awaits it —
    // exactly the same fire-and-forget shape _applyLayer()'s own
    // preload-then-swap already uses, guarded by the same `generation`
    // stale-response check so a slow load from a superseded render()
    // call can never clobber a newer one.
    //
    // data-canvas-frame (an <img>) gets the frame artwork itself via the
    // existing _applyLayer() helper — no new src-swap logic needed there.
    // data-canvas-frame-photo (a real <canvas>, see its own Liquid
    // comment) gets the effective photo, positioned via the SAME
    // compositePictureFramePhoto() primitive assets/bpf-memorial-
    // renderer.js's PNG export uses — read at runtime off
    // window.BpfMemorialRenderer (that script loads AFTER this one, but
    // render() only ever executes later, after every deferred script has
    // loaded — the exact same forward-reference shape this file's own
    // header already documents for window.BpfVaultEngravingLayer usage
    // elsewhere in this codebase).
    async _renderFrameLayers(state, generation) {
      if (!this.frameEl || !this.framePhotoCanvas) return;

      if (!state.pictureFrameSelected) {
        this._hideLayer(this.frameEl);
        this.framePhotoCanvas.hidden = true;
        this._clearFramePhotoCanvas();
        this._releaseFramePhotoObjectUrl();
        return;
      }

      const plan = BpfMemorialCanvasRenderer.resolveFrameLayerPlan(state);
      if (!plan) {
        this._hideLayer(this.frameEl);
        this.framePhotoCanvas.hidden = true;
        return;
      }

      this._applyLayer(this.frameEl, { src: plan.frameAssetSrc }, generation, '_lastFrameSrc');

      const renderer = window.BpfMemorialRenderer;
      if (!renderer || typeof renderer.loadImage !== 'function' || typeof renderer.compositePictureFramePhoto !== 'function') {
        this.framePhotoCanvas.hidden = true;
        return;
      }

      try {
        const framePromise = renderer.loadImage(plan.frameAssetSrc);
        const photoPromise = plan.hasCustomerPhoto
          ? Promise.resolve().then(() => renderer.loadImage(this._resolveFramePhotoObjectUrl(state.uploadedPhoto)))
          : renderer.loadImage(plan.placeholderAssetSrc);

        const [frameImg, photoImg] = await Promise.all([framePromise, photoPromise]);
        if (generation !== this._generation) return; // superseded by a newer render()

        this._clearFramePhotoCanvas();
        const ctx = this.framePhotoCanvas.getContext('2d');
        const box = { x: 0, y: 0, width: this.framePhotoCanvas.width, height: this.framePhotoCanvas.height };
        // Phase 4D.10C.1: only the placeholder path ever supplies a crop
        // — a real customer photo (plan.hasCustomerPhoto) is passed
        // through uncropped, exactly like the export path.
        const photoCrop = plan.hasCustomerPhoto ? undefined : plan.placeholderContentBounds;
        renderer.compositePictureFramePhoto(ctx, frameImg, box, plan.apertureNormalized, photoImg, photoCrop);
        this.framePhotoCanvas.hidden = false;
      } catch (error) {
        if (generation !== this._generation) return;
        this.framePhotoCanvas.hidden = true;
      }
    }

    _clearFramePhotoCanvas() {
      if (!this.framePhotoCanvas || typeof this.framePhotoCanvas.getContext !== 'function') return;
      const ctx = this.framePhotoCanvas.getContext('2d');
      if (ctx) ctx.clearRect(0, 0, this.framePhotoCanvas.width, this.framePhotoCanvas.height);
    }

    // Reuses the existing object URL for the SAME File reference (avoids
    // a redundant reload/flicker when render() re-runs for an unrelated
    // state change, e.g. an Orientation/Frame Color tweak with the photo
    // itself unchanged); creates and caches a fresh one, revoking
    // whatever preceded it, the moment the File reference itself changes
    // (a genuine replacement) — never leaks the previous URL.
    _resolveFramePhotoObjectUrl(file) {
      if (this._framePhotoFile === file && this._framePhotoObjectUrl) {
        return this._framePhotoObjectUrl;
      }
      this._releaseFramePhotoObjectUrl();
      this._framePhotoFile = file;
      this._framePhotoObjectUrl = URL.createObjectURL(file);
      return this._framePhotoObjectUrl;
    }

    // Applies one src entry to one layer <img>, preloading via a
    // detached Image() before ever touching the visible element's own
    // src — the visible <img> only ever receives a src that's already
    // confirmed to load successfully, so it can never show a
    // broken-image icon. `cacheKey` names the this[...] property used to
    // skip a redundant reload when the same src is already showing.
    //
    // Phase 3.0.1: no longer applies any per-entry placement — the
    // _positionLayer() method that used to do this is deleted, not just
    // unused. Every layer shares the exact same box, set once in CSS
    // (.bpf-memorial-canvas__layer), and the asset itself is now expected
    // to already be aligned to that shared canvas before it ever reaches
    // this file. This method's only job is src swapping.
    _applyLayer(imgEl, entry, generation, cacheKey) {
      if (!imgEl) return;

      if (!entry || !entry.src) {
        this._hideLayer(imgEl);
        this[cacheKey] = null;
        return;
      }

      if (this[cacheKey] === entry.src) {
        imgEl.hidden = false;
        return;
      }

      const loader = new Image();
      loader.onload = () => {
        if (generation !== this._generation) return; // superseded by a newer render()
        imgEl.src = entry.src;
        imgEl.hidden = false;
        this[cacheKey] = entry.src;
      };
      loader.onerror = () => {
        if (generation !== this._generation) return;
        this._hideLayer(imgEl);
        this[cacheKey] = null;
        this._showStatus('error');
      };
      loader.src = entry.src;
    }

    _hideLayer(imgEl) {
      if (!imgEl) return;
      imgEl.hidden = true;
    }

    _showStatus(type) {
      if (!this.statusEl) return;
      this.statusEl.hidden = false;
      this.statusEl.textContent =
        type === 'unavailable' ? 'This configuration is currently unavailable.' : 'Preview unavailable.';
      this.statusEl.classList.add('bpf-memorial-canvas__status--active');
    }

    _hideStatus() {
      if (!this.statusEl) return;
      this.statusEl.hidden = true;
      this.statusEl.classList.remove('bpf-memorial-canvas__status--active');
    }

    // Milestone 1 (Memorial Renderer): pure, static, and deliberately
    // knows nothing about the DOM — given a resolved visual record and
    // the currently-chosen colors, returns which layer(s) apply and in
    // what composition mode, exactly the same decision render() above
    // used to make inline. Extracted so assets/bpf-memorial-renderer.js
    // (a separate, commerce-agnostic rendering engine that composites
    // onto an offscreen canvas instead of these <img> elements) can ask
    // the identical question without a second copy of this branch ever
    // existing — see that file's own header. render() above is the only
    // caller inside this class; it still owns 100% of the DOM
    // application (hide/show/src-swap), unchanged in behavior.
    static resolveLayerPlan(visualAssets, colors) {
      const state = colors || {};
      const compositionMode = visualAssets ? visualAssets.compositionMode : null;

      if (compositionMode === 'flattened') {
        const capsuleEntry =
          visualAssets.flattenedAssetsByColor && state.capsuleColor
            ? visualAssets.flattenedAssetsByColor[state.capsuleColor]
            : null;
        return { compositionMode, base: null, figure: null, capsule: capsuleEntry || null };
      }

      const baseEntry =
        visualAssets && visualAssets.baseAssetsByColor && state.vaultColor
          ? visualAssets.baseAssetsByColor[state.vaultColor]
          : null;
      const figureEntry =
        visualAssets && visualAssets.figureAssetsByColor && state.figureColor
          ? visualAssets.figureAssetsByColor[state.figureColor]
          : null;
      return { compositionMode, base: baseEntry || null, figure: figureEntry || null, capsule: null };
    }

    // Phase 4D.10C, Part 22 — pure, static, DOM-free, same shape/purpose
    // as resolveLayerPlan() above: given canvasState (which already
    // carries pictureFrameSelected/Orientation/Color/Profiles — see
    // getCanvasState() in assets/bpf-memorial-designer.js), returns which
    // frame asset + aperture applies, and whether the customer's own
    // photo should be used instead of the orientation-specific
    // placeholder. Used by THIS class's own live render() below, the
    // same "pure decision here, DOM/canvas application in render()"
    // split resolveLayerPlan() already established. assets/bpf-memorial-
    // renderer.js's export path (drawPictureFrameLayer()) derives the
    // same facts inline instead of calling this — it draws frame+photo
    // directly onto one shared export canvas rather than two separate
    // elements, a different enough shape that duplicating this small,
    // pure lookup there was simpler than forcing a shared call site; the
    // actual PIXEL MATH (compositePictureFramePhoto()) is what's shared
    // between the two, not this resolution step.
    static resolveFrameLayerPlan(canvasState) {
      const state = canvasState || {};
      if (!state.pictureFrameSelected) return null;

      const profiles = state.pictureFrameProfiles;
      if (!profiles) return null;

      const orientationKey = (state.pictureFrameOrientation || 'Portrait').toLowerCase();
      const profile = profiles[orientationKey];
      if (!profile) return null;

      const frameAssetSrc = profile.standardAssets && profile.standardAssets[state.pictureFrameColor];
      if (!frameAssetSrc) return null;

      const hasCustomerPhoto = Boolean(state.uploadedPhoto);
      if (!hasCustomerPhoto && !profile.placeholderAsset) return null;

      return {
        frameAssetSrc,
        apertureNormalized: profile.aperture,
        hasCustomerPhoto,
        placeholderAssetSrc: profile.placeholderAsset || null,
        // Phase 4D.10C.1: the placeholder asset's own visible content is
        // a small sub-rectangle of a much larger canvas — see
        // placeholderContentBounds's own comment in sections/bpf-
        // memorial-designer.liquid. null/undefined-safe: only ever
        // applied on the placeholder path (see _renderFrameLayers()
        // below), never for a real customer photo.
        placeholderContentBounds: profile.placeholderContentBounds || null,
      };
    }
  };
}
